import { WASFATY_ALTERNATIVE_REASONS, type WasfatyAuthorizationDto, type WasfatyAuthorizeInput, type WasfatyLineDto, type WasfatyStatusDto, type WasfatyTradeDto } from '@his/shared';

export interface LineChoice {
  included: boolean;
  tradeCode: string;
  packs: number;
  alternativeReason: string;
}

export type Stage = 'choose' | 'awaiting' | 'claim' | 'declined' | 'done';

export const STATUS_TEXT: Record<WasfatyStatusDto, string> = {
  sending: 'Sending to Wasfaty',
  requested: 'Waiting for the payer',
  approved: 'Approved',
  partially_approved: 'Partly approved',
  rejected: 'Not approved',
  dispensed: 'Dispensed and claimed',
  cancelled: 'Released',
  expired: 'Hold ran out',
  refused: 'Refused by Wasfaty',
  unknown: 'Not confirmed by Wasfaty',
};

export function packsToCover(trade: WasfatyTradeDto): number {
  const minimum = trade.minimumPacks && trade.minimumPacks > 0 ? trade.minimumPacks : 1;
  if (trade.totalPacks && trade.totalPacks > 0) return Math.max(minimum, trade.totalPacks);
  const perPack = trade.minimumQuantity ?? 0;
  const total = trade.totalQuantity ?? 0;
  if (perPack <= 0 || total <= 0) return minimum;
  return Math.max(minimum, Math.floor(total / perPack));
}

export function preferredTrade(line: WasfatyLineDto): WasfatyTradeDto | undefined {
  return line.trades.find((trade) => !trade.isAlternative) ?? line.trades[0];
}

export function initialChoices(lines: readonly WasfatyLineDto[]): Record<number, LineChoice> {
  const choices: Record<number, LineChoice> = {};
  for (const line of lines) {
    const trade = preferredTrade(line);
    choices[line.lineNo] = { included: trade !== undefined, tradeCode: trade?.tradeCode ?? '', packs: trade ? packsToCover(trade) : 1, alternativeReason: '' };
  }
  return choices;
}

export function withTrade(line: WasfatyLineDto, choice: LineChoice, tradeCode: string): LineChoice {
  const trade = line.trades.find((entry) => entry.tradeCode === tradeCode);
  return { ...choice, tradeCode, packs: trade ? packsToCover(trade) : choice.packs, alternativeReason: trade?.isAlternative ? choice.alternativeReason : '' };
}

export function tradeOf(line: WasfatyLineDto, choice: LineChoice): WasfatyTradeDto | undefined {
  return line.trades.find((trade) => trade.tradeCode === choice.tradeCode);
}

export function lineTotalMinor(trade: WasfatyTradeDto | undefined, packs: number): number {
  if (!trade || !Number.isFinite(packs)) return 0;
  return Math.round((trade.singleUnitPrice ?? 0) * packs * 100);
}

export function choiceProblem(line: WasfatyLineDto, choice: LineChoice): string | null {
  const trade = tradeOf(line, choice);
  if (!trade) return 'Choose a medicine';
  const minimum = trade.minimumPacks && trade.minimumPacks > 0 ? trade.minimumPacks : 1;
  if (!Number.isInteger(choice.packs) || choice.packs < minimum) return `At least ${minimum} pack${minimum === 1 ? '' : 's'}`;
  if (trade.isAlternative && !WASFATY_ALTERNATIVE_REASONS.some((reason) => reason.code === choice.alternativeReason)) return 'Say why an alternative is dispensed';
  const perPack = trade.minimumQuantity ?? 0;
  const maximum = trade.totalQuantity ?? 0;
  if (maximum > 0 && perPack * choice.packs > maximum) return `More than the ${maximum} prescribed`;
  if (trade.isAlternative && maximum > 0 && perPack * choice.packs < maximum) return `An alternative has to cover all ${maximum}`;
  return null;
}

export function requestProblem(lines: readonly WasfatyLineDto[], choices: Record<number, LineChoice>): string | null {
  const included = lines.filter((line) => choices[line.lineNo]?.included);
  if (included.length === 0) return 'Tick at least one line';
  for (const line of included) {
    const problem = choiceProblem(line, choices[line.lineNo] as LineChoice);
    if (problem) return `${line.genericName ?? `Line ${line.lineNo}`}: ${problem}`;
  }
  return null;
}

export function requestFrom(lines: readonly WasfatyLineDto[], choices: Record<number, LineChoice>): WasfatyAuthorizeInput {
  return {
    lines: lines
      .filter((line) => choices[line.lineNo]?.included)
      .map((line) => {
        const choice = choices[line.lineNo] as LineChoice;
        const trade = tradeOf(line, choice);
        return {
          lineNo: line.lineNo,
          tradeCode: choice.tradeCode,
          packs: choice.packs,
          ...(trade?.isAlternative && choice.alternativeReason ? { alternativeReason: choice.alternativeReason } : {}),
        };
      }),
  };
}

export function totalMinor(lines: readonly WasfatyLineDto[], choices: Record<number, LineChoice>): number {
  return lines.reduce((sum, line) => {
    const choice = choices[line.lineNo];
    return choice?.included ? sum + lineTotalMinor(tradeOf(line, choice), choice.packs) : sum;
  }, 0);
}

export function stageOf(authorization: WasfatyAuthorizationDto | null): Stage {
  if (!authorization) return 'choose';
  switch (authorization.status) {
    case 'sending':
    case 'requested':
    case 'unknown':
      return 'awaiting';
    case 'approved':
    case 'partially_approved':
      return 'claim';
    case 'rejected':
      return 'declined';
    case 'dispensed':
      return 'done';
    default:
      return 'choose';
  }
}

export function holdCountdown(holdExpiresAt: string | null, now: number): { expired: boolean; label: string } {
  if (!holdExpiresAt) return { expired: false, label: '' };
  const left = new Date(holdExpiresAt).getTime() - now;
  if (left <= 0) return { expired: true, label: '0:00' };
  const seconds = Math.ceil(left / 1000);
  return { expired: false, label: `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}` };
}

export function approvedLines(authorization: WasfatyAuthorizationDto) {
  return authorization.lines.filter((line) => line.status === 'approved' || line.status === 'partially_approved');
}

export type Tone = 'success' | 'warning' | 'destructive' | 'secondary';

export function statusTone(status: WasfatyStatusDto): Tone {
  switch (status) {
    case 'approved':
    case 'dispensed':
      return 'success';
    case 'sending':
    case 'requested':
    case 'partially_approved':
    case 'unknown':
      return 'warning';
    case 'rejected':
    case 'refused':
    case 'expired':
      return 'destructive';
    default:
      return 'secondary';
  }
}
